
import { StudentWithAttendance } from '@/hooks/useReportsData';
import { LateRecord } from '@/hooks/useLateAttendance';
import { fetchSchoolInfo } from '@/services/schoolInfoService';
import { getCurrentDate, convertToArabicNumbers } from '@/utils/pdf/pdfUtils';
import { renderHtmlToPDF } from '@/utils/pdfRenderer';
import { dateSplitter } from './formatting/deteSplitter';

interface FilterOptions {
  selectedPeriod: string;
  selectedStage: string;
  selectedGrade: string;
  selectedSection: string;
}

interface StudentLateData {
  student: StudentWithAttendance;
  lateRecords: LateRecord[];
  totalLateDays: number;
  consecutiveLateDays: number;
}

export const generateLateAttendancePDF = async (
  studentsLateData: StudentLateData[],
  filters: FilterOptions
) => {
  try {
    console.log('🔥 lateAttendancePdfGenerator: بدء العملية');
    console.log('📊 عدد الطلاب المرسل:', studentsLateData.length);
    console.log('🎯 الفلاتر المرسلة:', filters);
    
    if (studentsLateData.length === 0) {
      console.error('❌ لا توجد بيانات طلاب للتصدير');
      throw new Error('لا توجد بيانات طلاب للتصدير');
    }
    
    console.log('🏫 جلب معلومات المدرسة...');
    const schoolInfo = await fetchSchoolInfo();
    console.log('✅ تم جلب معلومات المدرسة:', schoolInfo);
    
    console.log('📋 استخدام شعار وزارة التعليم...');
    const ministryLogoUrl = '/lovable-uploads/9af71c79-dc3c-4b30-b0d5-466d18153e1e.png';
    console.log('✅ تم تحديد شعار وزارة التعليم');
    
    const currentDate = getCurrentDate();
    console.log('📅 التاريخ الحالي:', currentDate);
    
    console.log('📄 إنشاء محتوى HTML...');
    const htmlContent = await generateLateAttendanceReportHtml(
      schoolInfo, 
      studentsLateData, 
      filters,
      ministryLogoUrl, 
      currentDate
    );
    console.log('✅ تم إنشاء محتوى HTML بنجاح');
    
    const fileName = `تقرير_التأخرات_${filters.selectedGrade || 'شامل'}_${dateSplitter(new Date())}.pdf`;
    console.log('📁 اسم الملف:', fileName);
    
    console.log('🖨️ تحويل HTML إلى PDF...');
    console.log('📄 HTML content length:', htmlContent.length);
    await renderHtmlToPDF(htmlContent, fileName);
    console.log('✅ تم تحويل HTML إلى PDF بنجاح');
    
    console.log('🎉 تم إكمال عملية إنشاء PDF بنجاح');
    return true;
    
  } catch (error) {
    console.error('❌ خطأ في إنشاء تقرير التأخرات PDF:', error);
    throw new Error(`فشل في إنشاء ملف PDF: ${error instanceof Error ? error.message : 'خطأ غير معروف'}`);
  }
};

const generateLateAttendanceReportHtml = async (
  schoolInfo: any,
  studentsLateData: StudentLateData[],
  filters: FilterOptions,
  ministryLogoUrl: string,
  currentDate: string
): Promise<string> => {
  const styles = getLateAttendanceReportStyles();
  
  // تقسيم الطلاب إلى صفحات (18 طالب لكل صفحة)
  const studentsPerPage = 18;
  const totalPages = Math.ceil(studentsLateData.length / studentsPerPage);
  console.log('📄 lateAttendancePdfGenerator: عدد الطلاب لكل صفحة:', studentsPerPage);
  console.log('📄 lateAttendancePdfGenerator: إجمالي الطلاب:', studentsLateData.length);
  console.log('📄 lateAttendancePdfGenerator: إجمالي الصفحات:', totalPages);
  
  // إنشاء صفحات الطلاب أولاً
  let studentPages = '';
  for (let page = 0; page < totalPages; page++) {
    const startIndex = page * studentsPerPage;
    const endIndex = Math.min(startIndex + studentsPerPage, studentsLateData.length);
    const pageStudents = studentsLateData.slice(startIndex, endIndex);
    
    studentPages += generateLateStudentPage(
      schoolInfo,
      ministryLogoUrl,
      currentDate,
      filters,
      pageStudents,
      page + 1,
      totalPages
    );
  }
  
  // إنشاء صفحة الإحصائيات بعد بيانات الطلاب
  const statsPage = generateLateAttendanceStatsPage(
    schoolInfo,
    ministryLogoUrl,
    currentDate,
    filters,
    studentsLateData
  );
  
  // إنشاء صفحة الرسم البياني أخيراً
  const chartPage = generateLateAttendanceChartPage(
    schoolInfo,
    ministryLogoUrl,
    currentDate,
    filters,
    studentsLateData
  );
  
  return `
    <!DOCTYPE html>
    <html dir="rtl" lang="ar">
    <head>
      <meta charset="UTF-8">
      <meta name="viewport" content="width=device-width, initial-scale=1.0">
      <title>تقرير التأخرات المفصل</title>
      ${styles}
    </head>
    <body>
      ${studentPages}
      ${statsPage}
      ${chartPage}
    </body>
    </html>
  `;
};

const generateLateHeader = (
  schoolInfo: any,
  ministryLogoUrl: string,
  currentDate: string,
  filters: FilterOptions
): string => {
  // تحويل الفترة للهجري إذا كانت تحتوي على تواريخ ميلادية
  let periodText = filters.selectedPeriod || 'جميع الفترات';
  if (periodText && periodText.includes('من') && periodText.includes('إلى')) {
    // إذا كانت الفترة تحتوي على تواريخ، تأكد من أنها بالأرقام الهندية
    periodText = convertToArabicNumbers(periodText);
  }
  return `<div class="header">
      <div class="header-section header-right">
        <div class="header-content">
          <div class="info-line">${schoolInfo.country || 'المملكة العربية السعودية'}</div>
          <div class="info-line">${schoolInfo.ministry || 'وزارة التعليم'}</div>
          <div class="info-line">${schoolInfo.education_department || 'إدارة التعليم'}</div>
          <div class="school-name">${schoolInfo.school_name}</div>
        </div>
      </div>
      <div class="header-section header-center">
        <img src="${ministryLogoUrl}" alt="شعار وزارة التعليم" class="ministry-logo">
      </div>
      <div class="header-section header-left">
        <div class="header-content">
          <div class="info-line">الصف: ${filters.selectedGrade || 'جميع الصفوف'}</div>
          <div class="info-line">الشعبة: ${filters.selectedSection || 'جميع الشعب'}</div>
          <div class="info-line">التاريخ: ${currentDate}</div>
          <div class="info-line">الفترة: ${periodText}</div>
        </div>
      </div>
    </div>
    <h2 class="report-title">تقرير التأخرات المفصل</h2>`;
};

const generateLateSummaryTable = (studentsLateData: StudentLateData[]): string => {
  const totalStudents = studentsLateData.length;
  const totalLateRecords = studentsLateData.reduce((sum, item) => sum + item.totalLateDays, 0);
  const studentsWithAlerts = studentsLateData.filter(item => item.consecutiveLateDays >= 3).length;
  const lateStudents = studentsLateData.filter(item => item.totalLateDays > 0).length;
  
  return `
    <div class="summary-section">
      <h3 class="section-title">ملخص إحصائيات التأخرات</h3>
      <table class="summary-table">
        <tr>
          <td class="summary-label">إجمالي الطلاب</td>
          <td class="summary-value">${convertToArabicNumbers(totalStudents.toString())}</td>
          <td class="summary-label">عدد التأخرات</td>
          <td class="summary-value">${convertToArabicNumbers(totalLateRecords.toString())}</td>
        </tr>
        <tr>
          <td class="summary-label">طلاب متأخرين</td>
          <td class="summary-value">${convertToArabicNumbers(lateStudents.toString())}</td>
          <td class="summary-label">طلاب بتنبيهات تأخر</td>
          <td class="summary-value">${convertToArabicNumbers(studentsWithAlerts.toString())}</td>
        </tr>
      </table>
    </div>
  `;
};

const generateLateStudentPage = (
  schoolInfo: any,
  ministryLogoUrl: string,
  currentDate: string,
  filters: FilterOptions,
  students: StudentLateData[],
  pageNumber: number,
  totalPages: number
): string => {
  const header = generateLateHeader(schoolInfo, ministryLogoUrl, currentDate, filters);
  const summary = pageNumber === 1 ? generateLateSummaryTable(students) : '';
  
  // Sort students by total late days (descending)
  const sortedStudents = [...students].sort((a, b) => b.totalLateDays - a.totalLateDays);
  
  const studentsTableRows = sortedStudents.map((item, index) => {
    let status = 'طبيعي';
    let statusClass = 'status-normal';
    
    if (item.consecutiveLateDays >= 5) {
      status = 'تحذير شديد';
      statusClass = 'status-severe';
    } else if (item.consecutiveLateDays >= 4) {
      status = 'تحذير عالي';
      statusClass = 'status-high';
    } else if (item.consecutiveLateDays >= 3) {
      status = 'تنبيه متوسط';
      statusClass = 'status-medium';
    } else if (item.totalLateDays >= 2) {
      status = 'تنبيه';
      statusClass = 'status-warning';
    }
    
    return `
      <tr>
        <td>${convertToArabicNumbers((((pageNumber - 1) * 18) + index + 1).toString())}</td>
        <td class="student-name">${item.student.name}</td>
        <td>${convertToArabicNumbers(item.totalLateDays.toString())}</td>
        <td>${convertToArabicNumbers(item.consecutiveLateDays.toString())}</td>
        <td class="${statusClass}">${status}</td>
      </tr>
    `;
  }).join('');
  
  return `
    <div class="page">
      ${header}
      ${summary}
      <div class="students-section">
        <h3 class="section-title">
          بيانات التأخرات 
          ${totalPages > 1 ? `(صفحة ${convertToArabicNumbers(pageNumber.toString())} من ${convertToArabicNumbers(totalPages.toString())})` : ''}
        </h3>
        <table class="students-table">
          <thead>
            <tr>
              <th>م</th>
              <th>اسم الطالب</th>
              <th>أيام التأخر</th>
              <th>تأخر متتالي</th>
              <th>الحالة</th>
            </tr>
          </thead>
          <tbody>
            ${studentsTableRows}
          </tbody>
        </table>
      </div>
    </div>
    ${totalPages > 0 ? '<div class="page-break"></div>' : ''}
  `;
};

const generateLateAttendanceStatsPage = (
  schoolInfo: any,
  ministryLogoUrl: string,
  currentDate: string,
  filters: FilterOptions,
  studentsLateData: StudentLateData[]
): string => {
  const header = generateLateHeader(schoolInfo, ministryLogoUrl, currentDate, filters);
  
  // حساب الطلاب الأكثر تأخراً (أعلى 10 طلاب)
  const topLateStudents = [...studentsLateData]
    .filter(item => item.totalLateDays > 0)
    .sort((a, b) => b.totalLateDays - a.totalLateDays)
    .slice(0, 10);
  
  // حساب الطلاب الأفضل حضوراً (الذين لم يتأخروا)
  const bestAttendanceStudents = [...studentsLateData]
    .filter(item => item.totalLateDays === 0)
    .slice(0, 10);
  
  // حساب التنبيهات (الطلاب الذين لديهم 3 أيام تأخير متتالية أو أكثر)
  const alertStudents = studentsLateData.filter(item => item.consecutiveLateDays >= 3);
  
  return `
    <div class="page">
      ${header}
      
      <div class="stats-page-content">
        <h2 class="stats-page-title">تقرير إحصائيات التأخر</h2>
        
        <div class="tables-container">          
          <!-- جدول الأفضل حضوراً والأكثر تأخراً -->
          <div class="best-late-tables">
            <div class="table-section right-table">
              <h3 class="table-title best-title">
                <span class="table-icon">✅</span>
                الأفضل حضوراً
              </h3>
              <table class="stats-table best-table">
                <thead>
                  <tr>
                    <th>م</th>
                    <th>اسم الطالب</th>
                    <th>عدد أيام التأخير</th>
                    <th>نسبة الحضور</th>
                  </tr>
                </thead>
                <tbody>
                  ${bestAttendanceStudents.length === 0 ? 
                    '<tr><td colspan="4" class="no-data">لا توجد بيانات</td></tr>' :
                    bestAttendanceStudents.map((item, index) => `
                       <tr>
                         <td>${convertToArabicNumbers((index + 1).toString())}</td>
                         <td class="student-name">${item.student.name}</td>
                         <td class="best-days">${convertToArabicNumbers('0')} متأخر</td>
                         <td class="percentage">${convertToArabicNumbers('100')}%</td>
                      </tr>
                    `).join('')
                  }
                </tbody>
              </table>
            </div>
            
            <div class="table-section left-table">
              <h3 class="table-title late-title">
                <span class="table-icon">⏰</span>
                الأكثر تأخراً
              </h3>
              <table class="stats-table late-table">
                <thead>
                  <tr>
                    <th>م</th>
                    <th>اسم الطالب</th>
                    <th>عدد أيام التأخير</th>
                    <th>نسبة التأخير</th>
                  </tr>
                </thead>
                <tbody>
                  ${topLateStudents.length === 0 ? 
                    '<tr><td colspan="4" class="no-data">لا توجد بيانات تأخر</td></tr>' :
                    topLateStudents.map((item, index) => {
                      const percentage = Math.round((item.totalLateDays / 30) * 100);
                      return `
                         <tr>
                           <td>${convertToArabicNumbers((index + 1).toString())}</td>
                           <td class="student-name">${item.student.name}</td>
                           <td class="late-days">${convertToArabicNumbers(item.totalLateDays.toString())} ${item.totalLateDays === 1 ? 'يوم' : 'أيام'}</td>
                           <td class="percentage">${convertToArabicNumbers(percentage.toString())}%</td>
                        </tr>
                      `;
                    }).join('')
                  }
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </div>
  `;
};

const generateLateAttendanceChartPage = (
  schoolInfo: any,
  ministryLogoUrl: string,
  currentDate: string,
  filters: FilterOptions,
  studentsLateData: StudentLateData[]
): string => {
  const header = generateLateHeader(schoolInfo, ministryLogoUrl, currentDate, filters);
  
  // حساب الإحصائيات
  const totalStudents = studentsLateData.length;
  const lateStudents = studentsLateData.filter(item => item.totalLateDays > 0).length;
  const notLateStudents = totalStudents - lateStudents;
  
  const latePercentage = totalStudents > 0 ? Math.round((lateStudents / totalStudents) * 100) : 0;
  const notLatePercentage = totalStudents > 0 ? Math.round((notLateStudents / totalStudents) * 100) : 0;

  // تحديد أقصى قيمة للرسم البياني
  const maxValue = Math.max(lateStudents, notLateStudents);
  const chartHeight = 300;
  
  // حساب ارتفاع الأعمدة
  const lateBarHeight = maxValue > 0 ? (lateStudents / maxValue) * chartHeight : 0;
  const notLateBarHeight = maxValue > 0 ? (notLateStudents / maxValue) * chartHeight : 0;

  return `
    <div style="page-break-before: always; padding: 30px 40px;">
      ${header}
      
      <div class="charts-page-title">الرسم البياني للتأخر من ${filters.selectedPeriod || 'لجميع الفترات'}</div>
      
      <div class="chart-page-content">
        
        <!-- البطاقات الإحصائية -->
        <div class="stats-cards-container">
          <div class="best-late-container">
            <div class="stat-card not-late-card">
              <div class="stat-content">
                <div class="stat-label">طالب غير متأخر</div>
                <div class="stat-percentage">${convertToArabicNumbers(notLatePercentage.toString())}% من المجموع</div>
              </div>
              <div class="stat-number">${convertToArabicNumbers(notLateStudents.toString())}</div>
            </div>
            
            <div class="stat-card late-card">
              <div class="stat-content">
                <div class="stat-label">طالب متأخر</div>
                <div class="stat-percentage">${convertToArabicNumbers(latePercentage.toString())}% من المجموع</div>
              </div>
              <div class="stat-number">${convertToArabicNumbers(lateStudents.toString())}</div>
            </div>
          </div>
          
          <div class="stat-card total-card">
            <div class="stat-content">
              <div class="stat-label">إجمالي عدد الطلاب</div>
              <div class="stat-percentage">${convertToArabicNumbers('100')}%</div>
            </div>
            <div class="stat-number">${convertToArabicNumbers(totalStudents.toString())}</div>
          </div>
        </div>
        
        <!-- الرسم البياني الدائري -->
        <div class="chart-container">
          <h3 class="chart-title">مقارنة الحضور والتأخر</h3>
          <div class="pie-chart-container">
            <svg class="pie-chart" viewBox="0 0 240 240" xmlns="http://www.w3.org/2000/svg">
              <!-- دائرة الخلفية -->
              <circle cx="120" cy="120" r="90" fill="#f0f0f0" stroke="#ddd" stroke-width="2"/>
              
              <!-- قسم غير المتأخرين -->
              <circle cx="120" cy="120" r="90" fill="none" stroke="#22c55e" stroke-width="20"
                      stroke-dasharray="${(notLatePercentage * 565.48) / 100} 565.48"
                      stroke-dashoffset="0" transform="rotate(-90 120 120)"/>
              
              <!-- قسم المتأخرين -->
              <circle cx="120" cy="120" r="90" fill="none" stroke="#ef4444" stroke-width="20"
                      stroke-dasharray="${(latePercentage * 565.48) / 100} 565.48"
                      stroke-dashoffset="-${(notLatePercentage * 565.48) / 100}" transform="rotate(-90 120 120)"/>
              
              <!-- النصوص داخل الدائرة -->
              <text x="120" y="115" text-anchor="middle" class="chart-center-text">المجموع</text>
              <text x="120" y="135" text-anchor="middle" class="chart-center-number">${convertToArabicNumbers(totalStudents.toString())}</text>
            </svg>
            
            <div class="pie-legend">
              <div class="legend-item">
                <div class="legend-color green"></div>
                <span>غير متأخر: ${convertToArabicNumbers(notLateStudents.toString())} طالب (${convertToArabicNumbers(notLatePercentage.toString())}%)</span>
              </div>
              <div class="legend-item">
                <div class="legend-color red"></div>
                <span>متأخر: ${convertToArabicNumbers(lateStudents.toString())} طالب (${convertToArabicNumbers(latePercentage.toString())}%)</span>
              </div>
            </div>
          </div>
        </div>
      </div>
      </div>
    </div>
  `;
};

const getLateAttendanceReportStyles = (): string => {
  return `
    <style>
      @import url('https://fonts.googleapis.com/css2?family=Tajawal:wght@400;500;700&display=swap');
      
      * {
        margin: 0;
        padding: 0;
        box-sizing: border-box;
      }
      
      body {
        font-family: 'Tajawal', 'Arial', sans-serif;
        direction: rtl;
        color: #1f2937;
        line-height: 1.6;
        font-size: 12px;
      }
      
      .page {
        width: 794px;
        min-height: 1123px;
        margin: 0 auto;
        padding: 0 40px 40px 40px;
        background: white;
        box-shadow: 0 0 10px rgba(0,0,0,0.1);
        page-break-after: always;
      }
      
      .page-break {
        page-break-before: always;
      }
      
      .header {
        display: grid;
        grid-template-columns: 1fr auto 1fr;
        align-items: start;
        gap: 20px;
        margin-bottom: 2px;
        padding-bottom: 8px;
        border-bottom: 2px solid #0DA9A6;
      }
      
      .header-section {
        display: flex;
        flex-direction: column;
        align-items: center;
      }
      
      .header-right {
        align-items: flex-end;
      }
      
      .header-left {
        align-items: flex-start;
      }
      
      .header-content {
        text-align: right;
      }
      
      .header-right .header-content {
        text-align: right;
      }
      
      .header-left .header-content {
        text-align: right;
      }
      
      .info-line {
        font-size: 14px;
        margin-bottom: 3px;
        color: #374151;
        font-weight: 500;
      }
      
      .school-name {
        font-size: 13px;
        font-weight: 700;
        color: #1f2937;
        margin-top: 8px;
      }
      
      .ministry-logo {
        width: 120px;
        height: 80px;
        object-fit: contain;
        margin-top: 10px;
      }
      
      .report-title {
        text-align: center;
        color: #174459;
        font-size: 12px;
        font-weight: 700;
        margin-top: -5px;
        margin-bottom: 3px;
        padding: 6px;
        border-radius: 8px;
        background: linear-gradient(135deg, rgba(23, 68, 89, 0.05) 0%, rgba(13, 169, 166, 0.05) 100%);
      }
      
      .summary-section {
        margin-top: -10px;
        margin-bottom: 5px;
        padding: 0;
        border: none;
        border-radius: 0;
        background: none;
      }
      
      .section-title {
        color: #0DA9A6;
        font-size: 14px;
        font-weight: 700;
        margin-bottom: 2px;
        text-align: center;
      }
      
      .summary-table {
        width: 100%;
        border-collapse: collapse;
      }
      
      .summary-table td {
        padding: 8px 12px;
        border: 1px solid #e5e7eb;
        font-size: 12px;
      }
      
      .summary-label {
        background-color: #f9fafb;
        font-weight: 600;
        color: #374151;
        text-align: right;
      }
      
      .summary-value {
        background-color: white;
        font-weight: 700;
        color: #0DA9A6;
        text-align: center;
      }
      
      .students-section {
        margin-bottom: 20px;
      }
      
      .students-table {
        width: 100%;
        border-collapse: collapse;
        font-size: 12px;
        margin-top: 0px;
      }
      
      .students-table th {
        background-color: #0DA9A6;
        color: white;
        padding: 4px 6px;
        text-align: center;
        font-weight: 600;
        border: 1px solid #000;
      }
      
      .students-table td {
        padding: 3px 6px;
        border: 1px solid #000;
        text-align: center;
        color: #374151;
      }
      
      .students-table tbody tr:nth-child(even) {
        background-color: rgba(249, 250, 251, 0.5);
      }
      
      .student-name {
        text-align: right !important;
        font-weight: 500;
        padding-right: 12px !important;
      }
      
      .status-normal {
        color: #059669;
        font-weight: 600;
      }
      
      .status-warning {
        color: #0D9488;
        font-weight: 600;
      }
      
      .status-medium {
        color: #2563EB;
        font-weight: 600;
      }
      
      .status-high {
        color: #D97706;
        font-weight: 600;
      }
      
      .status-severe {
        color: #DC2626;
        font-weight: 600;
       }
      }
      .stats-page-content {
        padding: 10px 0;
      }
      
      .stats-page-title {
        text-align: center;
        color: #174459;
        font-size: 18px;
        font-weight: 700;
        margin-bottom: 20px;
        padding: 10px;
        border-radius: 8px;
        background: linear-gradient(135deg, rgba(23, 68, 89, 0.05) 0%, rgba(13, 169, 166, 0.05) 100%);
      }
      
      .tables-container {
        display: flex;
        flex-direction: column;
        gap: 15px;
      }
      
      .table-section {
        margin-bottom: 10px;
        page-break-inside: avoid;
        break-inside: avoid;
      }
      
      .table-title {
        display: flex;
        align-items: center;
        gap: 10px;
        font-size: 14px;
        font-weight: 700;
        margin-bottom: 5px;
        padding: 8px;
        border-radius: 8px;
      }
      
      .alert-title {
        background: rgba(239, 68, 68, 0.1);
        color: #dc2626;
        border: 1px solid #fecaca;
      }
      
      .late-title {
        background: rgba(249, 115, 22, 0.1);
        color: #ea580c;
        border: 1px solid #fed7aa;
      }
      
      .best-title {
        background: rgba(34, 197, 94, 0.1);
        color: #16a34a;
        border: 1px solid #bbf7d0;
      }
      
      .table-icon {
        font-size: 16px;
      }
       
      .stats-table {
        width: 100%;
        border-collapse: collapse;
        font-size: 11px;
        margin-top: 5px;
        page-break-inside: avoid;
        break-inside: avoid;
      }
      
      .stats-table th {
        background-color: #f8fafc;
        color: #374151;
        padding: 10px 8px;
        text-align: center;
        font-weight: 600;
        border: 1px solid #000;
      }
      
      .alert-table th {
        background-color: rgba(239, 68, 68, 0.1);
      }
      
      .late-table th {
        background-color: rgba(249, 115, 22, 0.1);
      }
      
      .best-table th {
        background-color: rgba(34, 197, 94, 0.1);
      }
      
      .stats-table td {
        padding: 4px 6px;
        border: 1px solid #000;
        text-align: center;
        color: #374151;
        height: 20px;
      }
      
      .stats-table tbody tr:nth-child(even) {
        background-color: rgba(249, 250, 251, 0.5);
      }
      
      .alert-days {
        color: #dc2626;
        font-weight: 600;
      }
      
      .late-days {
        color: #ea580c;
        font-weight: 600;
      }
      
      .best-days {
        color: #16a34a;
        font-weight: 600;
      }
      
      .percentage {
        font-weight: 600;
      }
      
      .no-data {
        text-align: center !important;
        color: #9ca3af;
        font-style: italic;
        padding: 20px !important;
      }
      .chart-page-content {
        padding: 20px 30px;
        margin: 20px 0;
      }
      
      .charts-page-title {
        text-align: center;
        color: #174459;
        font-size: 18px;
        font-weight: 700;
        margin: 30px 0 40px 0;
        padding: 0 20px;
      }
        padding: 10px;
        border-radius: 8px;
        background: linear-gradient(135deg, rgba(23, 68, 89, 0.05) 0%, rgba(13, 169, 166, 0.05) 100%);
      }
      
      .chart-title {
        text-align: center;
        color: #174459;
        font-size: 20px;
        font-weight: 700;
        margin-bottom: 20px;
        padding: 10px;
        border-radius: 8px;
        background: linear-gradient(135deg, rgba(23, 68, 89, 0.05) 0%, rgba(13, 169, 166, 0.05) 100%);
      }
      
      .stats-cards-container {
        display: flex;
        flex-direction: row;
        gap: 15px;
        margin: 30px 20px 40px 20px;
      }
      
      .stat-card {
        flex: 1;
        display: flex;
        align-items: center;
        gap: 8px;
        padding: 8px 10px;
        border-radius: 6px;
        border: 1px solid;
        height: 45px;
        min-width: 0;
      }
      
      .best-late-container {
        display: contents;
      }
      
      .best-late-container .stat-card {
        flex: 1;
      }
      
      .best-late-tables {
        display: flex;
        gap: 20px;
        margin-bottom: 30px;
      }
      
      .left-table, .right-table {
        flex: 1;
      }
      
      .late-card {
        background: rgba(251, 146, 60, 0.1);
        border-color: #fb923c;
      }
      
      .not-late-card {
        background: rgba(34, 197, 94, 0.1);
        border-color: #22c55e;
      }
      
      .total-card {
        background: rgba(107, 114, 128, 0.1);
        border-color: #6b7280;
      }
      
      .stat-icon {
        font-size: 16px;
        width: 24px;
        height: 24px;
        display: flex;
        align-items: center;
        justify-content: center;
        border-radius: 50%;
        flex-shrink: 0;
      }
      
      .late-icon {
        background: #fb923c;
        color: white;
      }
      
      .not-late-icon {
        background: #22c55e;
        color: white;
      }
      
      .total-icon {
        background: #6b7280;
        color: white;
      }
      
      .stat-content {
        flex: 1;
        text-align: center;
        padding: 0 10px;
      }
      
      .stat-number {
        font-size: 20px;
        font-weight: 700;
        text-align: left;
        min-width: 35px;
      }
      
      .late-card .stat-number {
        color: #ea580c;
      }
      
      .not-late-card .stat-number {
        color: #16a34a;
      }
      
      .total-card .stat-number {
        color: #4b5563;
      }
      
      .stat-label {
        font-size: 11px;
        font-weight: 600;
        margin-bottom: 2px;
        color: #374151;
      }
      
      .stat-percentage {
        font-size: 9px;
        color: #6b7280;
        margin-top: 1px;
      }
      
      .chart-container {
        background: #f8fafc;
        border: 1px solid #e2e8f0;
        border-radius: 12px;
        padding: 40px 30px;
        margin: 40px auto;
        max-width: 500px;
        text-align: center;
      }
      
      .pie-chart-container {
        display: flex;
        flex-direction: column;
        align-items: center;
        gap: 20px;
      }
      
      .pie-chart {
        width: 240px;
        height: 240px;
        filter: drop-shadow(0 4px 8px rgba(0,0,0,0.1));
      }
      
      .chart-center-text {
        font-size: 14px;
        font-weight: 600;
        fill: #374151;
      }
      
      .chart-center-number {
        font-size: 20px;
        font-weight: 700;
        fill: #1f2937;
      }
      
      .pie-legend {
        display: flex;
        gap: 30px;
        justify-content: center;
        align-items: center;
      }
      
      .legend-item {
        display: flex;
        align-items: center;
        gap: 8px;
        font-size: 14px;
        font-weight: 600;
      }
      
      .legend-color {
        width: 18px;
        height: 18px;
        border-radius: 3px;
        border: 1px solid #ddd;
      }
      
      .legend-color.green {
        background-color: #22c55e;
      }
      
      .legend-color.red {
        background-color: #ef4444;
      }
      
      .chart-area {
        display: flex;
        height: 350px;
        align-items: end;
        width: 100%;
      }
      
      .chart-plot {
        flex: 1;
        height: 300px;
        position: relative;
      }
      
      .grid-lines {
        position: absolute;
        top: 0;
        left: 0;
        right: 0;
        height: 100%;
        display: flex;
        flex-direction: column;
        justify-content: space-between;
      }
      
      .grid-line {
        height: 1px;
        background: #cbd5e1;
        opacity: 0.7;
      }
      
      .bars-container {
        display: flex;
        justify-content: space-around;
        align-items: end;
        height: 100%;
        padding: 0 50px;
        position: absolute;
        bottom: 0;
        width: 100%;
      }
      
      .bar-group {
        display: flex;
        flex-direction: column;
        align-items: center;
        gap: 10px;
      }
      
      .bar {
        width: 80px;
        position: relative;
        border-radius: 4px 4px 0 0;
        display: flex;
        align-items: flex-start;
        justify-content: center;
        padding-top: 8px;
        margin-bottom: 0;
      }
      
      .late-bar {
        background: #fb923c;
      }
      
      .not-late-bar {
        background: #22c55e;
      }
      
      .bar-value {
        font-size: 14px;
        font-weight: 700;
        color: white;
      }
      
      .bar-label {
        font-size: 14px;
        font-weight: 600;
        color: #374151;
        text-align: center;
        margin-top: 10px;
      }
      
      .x-axis-label {
        text-align: center;
        margin-top: 20px;
        font-size: 16px;
        font-weight: 700;
        color: #1e293b;
      }
      
      @media print {
        .page {
          box-shadow: none;
          page-break-after: always;
        }
        
        body {
          print-color-adjust: exact;
          -webkit-print-color-adjust: exact;
        }
      }
    </style>
  `;
};
